// app/src/lib/scale/approvals.js — approval workflow (US-5.4).
//
// Flow:
//   1. A PO or invoice payment crosses the threshold (default $500). The author
//      calls `requestApproval(entityType, entityId, amountCents)`. The entity's
//      `status` is set to `PENDING_APPROVAL`; an `approvalRequests` row is
//      created in `pending` state.
//   2. An Owner or Manager calls `approve(requestId, note?)` or `reject(...)`.
//   3. On approve: entity returns to its normal next state (`SUBMITTED` for POs,
//      `PAID_LOCAL` for invoices). On reject: entity goes back to `DRAFT` with
//      a rejection note attached.
//   4. Every action is audit-logged.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { effectiveRole, gate } from './members.js';
import * as audit from './audit.js';
import { can } from './rbac.js';

const DEFAULT_THRESHOLD_CENTS = 500_00;

/** @typedef {'pending'|'approved'|'rejected'} ApprovalStatus */
/** @typedef {{ requestId:string, ownerId:string, entityType:'purchaseOrder'|'invoice', entityId:string, amountCents:number, requesterId:string, status:ApprovalStatus, approverId?:string, note?:string, createdAt:number, decidedAt?:number }} ApprovalRequest */

export async function thresholdCents() { return DEFAULT_THRESHOLD_CENTS; }

/** Create an approval request and transition the entity to PENDING_APPROVAL. */
export async function requestApproval(entityType, entityId, amountCents, ownerId) {
  const claims = await currentClaims();
  if (!claims) throw new Error('approval: not signed in');
  const requesterId = claims.sub;
  const oid = ownerId || requesterId;
  if (!['purchaseOrder', 'invoice'].includes(entityType)) throw new Error('approval: invalid entityType');

  // Look up the entity to verify ownership before flipping status.
  const table = entityType === 'purchaseOrder' ? db.purchaseOrders : db.invoices;
  const entity = await table.get(entityId);
  if (!entity || entity.userId !== oid) throw new Error('approval: entity not found');

  const requestId = crypto.randomUUID();
  /** @type {ApprovalRequest} */
  const req = {
    requestId, ownerId: oid, entityType, entityId,
    amountCents: Math.round(amountCents),
    requesterId, status: 'pending',
    createdAt: Date.now(),
  };

  await db.transaction('rw', table, db.approvalRequests, async () => {
    entity.status = 'PENDING_APPROVAL';
    entity.pendingApprovalId = requestId;
    await table.put(entity);
    await db.approvalRequests.add(req);
  });
  await audit.record({ ownerId: oid, action: 'create', entityType: 'approvalRequest', entityId: requestId, after: req });
  return req;
}

export async function approve(requestId, note) {
  return decide(requestId, 'approved', note);
}
export async function reject(requestId, note) {
  return decide(requestId, 'rejected', note);
}

async function decide(requestId, decision, note) {
  const claims = await currentClaims();
  if (!claims) throw new Error('approval: not signed in');
  const req = await db.approvalRequests.get(requestId);
  if (!req) throw new Error('approval: not found');
  if (req.status !== 'pending') throw new Error('approval: already decided');

  // Only Owner or Manager can decide — gated via RBAC.
  await gate(req.ownerId, 'po.approve'); // same capability gates both PO + invoice.
  if (req.requesterId === claims.sub) {
    // Defense-in-depth: an approver cannot self-approve their own request.
    throw new Error('approval: cannot self-approve');
  }

  const table = req.entityType === 'purchaseOrder' ? db.purchaseOrders : db.invoices;
  await db.transaction('rw', table, db.approvalRequests, async () => {
    const entity = await table.get(req.entityId);
    if (!entity) throw new Error('approval: entity gone');
    if (decision === 'approved') {
      entity.status = req.entityType === 'purchaseOrder' ? 'SUBMITTED' : 'PAID_LOCAL';
    } else {
      entity.status = 'DRAFT';
      entity.rejectionNote = note;
    }
    delete entity.pendingApprovalId;
    await table.put(entity);

    req.status = decision;
    req.approverId = claims.sub;
    req.note = note;
    req.decidedAt = Date.now();
    await db.approvalRequests.put(req);
  });

  await audit.record({
    ownerId: req.ownerId,
    action: decision === 'approved' ? 'approve' : 'reject',
    entityType: 'approvalRequest', entityId: requestId,
    meta: { entityType: req.entityType, entityId: req.entityId, note },
  });
  return req;
}

export async function listPending(ownerId) {
  return db.approvalRequests
    .where('[ownerId+status]').equals([ownerId, 'pending'])
    .toArray();
}
